import { CareOSLoader } from '@/components/ui/careos-loader';
import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useShift } from '@/hooks/useShift';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SignedAvatar } from '@/components/ui/signed-avatar';
import { ShiftTasksPreview } from '@/components/shifts/ShiftTasksPreview';
import { Badge } from '@/components/ui/badge';
import { AppLayout } from '@/components/layout/AppLayout';
import {
    LogIn, CheckCircle2, Pill, FileText, MapPin, Loader2, Clock,
    ListTodo, Utensils, Droplet, Activity, ArrowRightLeft, User
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';
import { ShiftTemplate, getShiftHoursForDay, formatTimeDisplay } from '@/types/shift-templates';
import { Tables } from '@/integrations/supabase/types';
import { usePatientRole } from '@/hooks/usePatientRole';
import { isPatientInactiveRlsError, PATIENT_INACTIVE_TOAST_MESSAGE } from '@/lib/patient-status-errors';

interface LastShiftInfo {
    id: string;
    check_in_at: string;
    check_out_at: string | null;
    caregiver_name: string | null;
    ai_summary: string | null;
    handoff?: {
        text_summary: string | null;
        tasks_completed: number;
        tasks_total: number;
        sporadic_meds_given: number;
    } | null;
}

interface ShiftTaskPreview {
    id: string;
    name: string;
    task_type: string;
    schedule_times: string[] | null;
    filtered_times: string[];
}

export default function ShiftCheckInPage() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { patientId } = useParams<{ patientId: string }>();
    const { checkIn } = useShift();
    const { user } = useAuth();
    const [searchParams] = useSearchParams();
    const swapTemplateId = searchParams.get('swapTemplateId');
    const { isPatientActive, status: patientStatus } = usePatientRole(patientId);

    const [isLoading, setIsLoading] = useState(false);
    const [patient, setPatient] = useState<{ nickname: string; photo_url: string | null } | null>(null);
    const [lastShift, setLastShift] = useState<LastShiftInfo | null>(null);
    const [loadingLastShift, setLoadingLastShift] = useState(true);
    const [location, setLocation] = useState<{ latitude: number; longitude: number } | null>(null);
    const [locationLoading, setLocationLoading] = useState(true);
    const [locationError, setLocationError] = useState<string | null>(null);

    // Shift template and tasks preview
    const [ownShiftTemplate, setOwnShiftTemplate] = useState<ShiftTemplate | null>(null);
    const [swappedShiftTemplate, setSwappedShiftTemplate] = useState<ShiftTemplate | null>(null);
    const [allTasks, setAllTasks] = useState<Tables<'care_plan_tasks'>[]>([]);
    const [loadingPreview, setLoadingPreview] = useState(true);

    const dateLocale = i18n.language?.startsWith('pt') ? ptBR : enUS;
    const today = new Date();
    const dayOfWeek = today.getDay();

    const activeShiftTemplate = swapTemplateId ? swappedShiftTemplate : ownShiftTemplate;

    // Get shift hours for today
    const shiftHours = useMemo(() => {
        if (!activeShiftTemplate) return null;
        return getShiftHoursForDay(activeShiftTemplate, dayOfWeek);
    }, [activeShiftTemplate, dayOfWeek]);

    // Per-row filtered tasks (preserves original task IDs for instantiation)
    const filteredRows = useMemo(() => {
        if (!allTasks.length) return [] as { task: Tables<'care_plan_tasks'>; filteredTimes: string[] }[];

        const todayTasks = allTasks.filter(task => {
            if (!task.is_active || task.is_sporadic) return false;
            if (!task.schedule_days?.includes(dayOfWeek)) return false;
            return true;
        });

        const shiftStart = shiftHours?.start;
        const shiftEnd = shiftHours?.end;
        const isOvernight = !!shiftHours && (activeShiftTemplate?.is_overnight || (shiftStart! > shiftEnd!));

        return todayTasks.map(task => {
            const times = (task.schedule_times || []).map(t => t.substring(0, 5));
            const filteredTimes = !shiftHours
                ? times
                : times.filter(taskTime => {
                    if (isOvernight) {
                        return taskTime >= shiftStart! || taskTime < shiftEnd!;
                    }
                    return taskTime >= shiftStart! && taskTime < shiftEnd!;
                });
            return { task, filteredTimes };
        }).filter(x => x.filteredTimes.length > 0);
    }, [allTasks, shiftHours, activeShiftTemplate, dayOfWeek]);

    // Grouped tasks for display (merge same name+type rows so all times are visible)
    const filteredTasks = useMemo((): ShiftTaskPreview[] => {
        const groupsMap = new Map<string, ShiftTaskPreview>();
        filteredRows.forEach(({ task, filteredTimes }) => {
            const key = `${task.task_type}__${task.name}`;
            const existing = groupsMap.get(key);
            if (existing) {
                existing.filtered_times = Array.from(new Set([...existing.filtered_times, ...filteredTimes]));
            } else {
                groupsMap.set(key, {
                    id: task.id,
                    name: task.name,
                    task_type: task.task_type,
                    schedule_times: task.schedule_times,
                    filtered_times: [...filteredTimes],
                });
            }
        });

        const groups = Array.from(groupsMap.values()).map(g => ({
            ...g,
            filtered_times: [...g.filtered_times].sort(),
        }));
        groups.sort((a, b) => {
            const at = a.filtered_times[0] || '';
            const bt = b.filtered_times[0] || '';
            if (at !== bt) return at.localeCompare(bt);
            return a.name.localeCompare(b.name);
        });
        return groups;
    }, [filteredRows]);

    // Count total task instances (sum across grouped times)
    const totalTaskInstances = useMemo(() => {
        return filteredTasks.reduce((sum, task) => sum + task.filtered_times.length, 0);
    }, [filteredTasks]);

    useEffect(() => {
        if (patientId) {
            fetchPatient();
            fetchLastShift();
            fetchShiftPreview();
            requestLocation();
        }
    }, [patientId, user?.id]);

    useEffect(() => {
        if (swapTemplateId && patientId) {
            fetchSwappedTemplate(swapTemplateId);
        }
    }, [swapTemplateId, patientId]);

    const fetchPatient = async () => {
        if (!patientId) return;
        const { data } = await supabase
            .from('patients')
            .select('nickname, photo_url')
            .eq('id', patientId)
            .single();
        if (data) setPatient(data);
    };

    const fetchSwappedTemplate = async (templateId: string) => {
        try {
            const { data: templateData } = await supabase
                .from('shift_templates')
                .select('*')
                .eq('id', templateId)
                .single();

            if (templateData) {
                setSwappedShiftTemplate({
                    id: templateData.id,
                    name: templateData.name,
                    patient_id: patientId!,
                    default_start_time: templateData.default_start_time,
                    default_end_time: templateData.default_end_time,
                    is_overnight: templateData.is_overnight || false,
                    has_variable_hours: templateData.has_variable_hours || false,
                    day_specific_times: (templateData.day_specific_times as any) || {},
                    created_at: templateData.created_at,
                    updated_at: templateData.updated_at,
                });
            }
        } catch (e) {
            console.error("Error fetching swapped template", e);
        }
    };

    const fetchShiftPreview = async () => {
        if (!user?.id || !patientId) return;

        try {
            const [linkResult, tasksResult] = await Promise.all([
                supabase
                    .from('patient_user_links')
                    .select('shift_template_id')
                    .eq('patient_id', patientId)
                    .eq('user_id', user.id)
                    .eq('role', 'caregiver')
                    .maybeSingle(),
                supabase
                    .from('care_plan_tasks')
                    .select('*')
                    .eq('patient_id', patientId)
                    .eq('is_active', true)
                    .eq('is_sporadic', false)
                    .order('name'),
            ]);

            if (linkResult.data?.shift_template_id) {
                const { data: templateData } = await supabase
                    .from('shift_templates')
                    .select('*')
                    .eq('id', linkResult.data.shift_template_id)
                    .single();

                if (templateData) {
                    setOwnShiftTemplate({
                        id: templateData.id,
                        name: templateData.name,
                        patient_id: patientId,
                        default_start_time: templateData.default_start_time,
                        default_end_time: templateData.default_end_time,
                        is_overnight: templateData.is_overnight || false,
                        has_variable_hours: templateData.has_variable_hours || false,
                        day_specific_times: (templateData.day_specific_times as any) || {},
                        created_at: templateData.created_at,
                        updated_at: templateData.updated_at,
                    });
                }
            }

            if (tasksResult.data) {
                setAllTasks(tasksResult.data);
            }
        } catch (error) {
            console.error('Error fetching shift preview:', error);
        } finally {
            setLoadingPreview(false);
        }
    };

    const requestLocation = () => {
        if (!navigator.geolocation) {
            setLocationError(t('shift.locationNotSupported'));
            setLocationLoading(false);
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                setLocation({
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                });
                setLocationLoading(false);
            },
            (error) => {
                console.error('Geolocation error:', error);
                setLocationError(t('shift.locationDenied'));
                setLocationLoading(false);
            },
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 0,
            }
        );
    };

    const fetchLastShift = async () => {
        if (!patientId) return;
        try {
            const { data: shiftData, error: shiftError } = await supabase
                .from('shifts')
                .select('id, check_in_at, check_out_at, caregiver_id, ai_summary')
                .eq('patient_id', patientId)
                .eq('status', 'completed')
                .order('check_out_at', { ascending: false })
                .limit(1)
                .maybeSingle();

            if (shiftError || !shiftData) {
                setLoadingLastShift(false);
                return;
            }

            const [profileResult, handoffResult] = await Promise.all([
                supabase
                    .from('profiles')
                    .select('full_name')
                    .eq('user_id', shiftData.caregiver_id)
                    .limit(1)
                    .maybeSingle(),
                supabase
                    .from('handoffs')
                    .select('text_summary, tasks_completed, tasks_total, sporadic_meds_given')
                    .eq('shift_id', shiftData.id)
                    .limit(1)
                    .maybeSingle(),
            ]);

            setLastShift({
                id: shiftData.id,
                check_in_at: shiftData.check_in_at,
                check_out_at: shiftData.check_out_at,
                caregiver_name: profileResult.data?.full_name || null,
                ai_summary: shiftData.ai_summary,
                handoff: handoffResult.data || null,
            });
        } catch (error) {
            console.error('Error fetching last shift:', error);
        } finally {
            setLoadingLastShift(false);
        }
    };

    const handleSubmit = async () => {
        if (!patientId) return;
        if (!isPatientActive) {
            toast.error(PATIENT_INACTIVE_TOAST_MESSAGE);
            return;
        }
        setIsLoading(true);

        const isSwapMode = !!swapTemplateId;

        // Prepare task instances for instantiation
        // We use the same filteredTasks logic used for the UI preview
        const taskInstancesArr: { taskId: string, scheduledFor: string }[] = [];
        const checkInDate = new Date();

        // We need to match the logic in the SQL instantiate_shift_tasks function
        // 1. Get current date in Brazil timezone (roughly, since JS Date is local)
        // 2. Loop through filtered tasks and their times
        // 3. If overnight and time < shift_start, it's for the next day

        if (filteredRows.length > 0) {
            filteredRows.forEach(({ task, filteredTimes }) => {
                filteredTimes.forEach(timeStr => {
                    const taskDate = new Date(checkInDate);
                    const [hours, minutes] = timeStr.split(':').map(Number);

                    taskDate.setHours(hours, minutes, 0, 0);

                    if (activeShiftTemplate?.is_overnight && shiftHours) {
                        const shiftStartHours = parseInt(shiftHours.start.split(':')[0]);
                        if (hours < shiftStartHours) {
                            taskDate.setDate(taskDate.getDate() + 1);
                        }
                    }

                    taskInstancesArr.push({
                        taskId: task.id,
                        scheduledFor: taskDate.toISOString()
                    });
                });
            });
        }

        try {
            const shift = await checkIn({
                patientId,
                photoUrl: null,
                latitude: location?.latitude ?? null,
                longitude: location?.longitude ?? null,
                shiftTemplateId: isSwapMode ? swapTemplateId : ownShiftTemplate?.id,
                isSwap: isSwapMode,
                originalTemplateId: isSwapMode ? ownShiftTemplate?.id || null : null,
                taskInstances: taskInstancesArr
            });

            if (shift) {
                toast.success(t('shift.checkInSuccess'));
                navigate(`/app/caregiver/${patientId}`);
            } else {
                toast.error(t('caregiver.duplicateShiftError'));
            }
        } catch (error: any) {
            console.error('Error:', error);
            if (error.message === 'PATIENT_ALREADY_HAS_ACTIVE_SHIFT') {
                toast.error(t('caregiver.patientAlreadyHasActiveShift'));
            } else if (isPatientInactiveRlsError(error)) {
                toast.error(PATIENT_INACTIVE_TOAST_MESSAGE);
            } else {
                toast.error(error.message || t('shift.checkInError'));
            }
        } finally {
            setIsLoading(false);
        }
    };

    const getTaskIcon = (taskType: string) => {
        switch (taskType) {
            case 'fixed_medication': return <Pill className="w-4 h-4" />;
            case 'food': return <Utensils className="w-4 h-4" />;
            case 'hygiene': return <Droplet className="w-4 h-4" />;
            case 'vital_sign': return <Activity className="w-4 h-4" />;
            default: return <ListTodo className="w-4 h-4" />;
        }
    };

    return (
        <AppLayout title={t('shift.checkIn')} showBack onBack={() => navigate(-1)}>
            <div className="p-4 space-y-4 pb-4">
                {/* Header User/Patient Info */}
                <div className="flex items-center gap-3 p-4 bg-muted/10 rounded-lg border border-border/50">
                    <SignedAvatar
                        src={patient?.photo_url || null}
                        bucket="patient-photos"
                        className="w-12 h-12 ring-2 ring-primary/10"
                        fallback={
                            <span className="bg-primary/10 text-primary w-full h-full flex items-center justify-center rounded-full text-lg">
                                {patient?.nickname?.charAt(0) || '?'}
                            </span>
                        }
                    />
                    <div>
                        <h2 className="font-semibold text-lg">{patient?.nickname}</h2>
                        <p className="text-sm text-muted-foreground">{t('shift.startingShift')}</p>
                    </div>
                </div>

                {/* Shift Template Preview */}
                <Card className="border-2 border-muted/40 bg-muted/5 shadow-sm">
                    <CardHeader className="pb-2 pt-4">
                        <CardTitle className="text-base flex items-center justify-between flex-wrap gap-2">
                            <div className="flex items-center gap-2">
                                <Clock className="w-5 h-5 text-primary" />
                                {loadingPreview ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <span className="font-semibold">
                                        {activeShiftTemplate ? activeShiftTemplate.name : t('shiftTemplates.noTemplateAssigned')}
                                    </span>
                                )}
                            </div>
                            {shiftHours && (
                                <Badge variant="secondary" className="text-xs px-2 py-0.5">
                                    {formatTimeDisplay(shiftHours.start)} - {formatTimeDisplay(shiftHours.end)}
                                </Badge>
                            )}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pb-4">
                        {loadingPreview ? (
                            <div className="flex items-center justify-center py-6">
                                <CareOSLoader size="md" />
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {swapTemplateId && (
                                    <>
                                         <div className="p-3 bg-sporadic/10 border border-sporadic/20 rounded-lg mb-3">
                                            <p className="text-sm text-sporadic font-medium text-center">
                                                {t('shiftSwap.assumingColleagueShift')}
                                            </p>
                                        </div>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="w-full border-sporadic/20 text-sporadic hover:bg-sporadic/10"
                                            onClick={() => navigate(`/app/patients/${patientId}/check-in`)}
                                        >
                                            {t('shiftSwap.cancelSwap')}
                                        </Button>
                                    </>
                                )}

                                <div className="pt-1">
                                    <p className="text-sm text-muted-foreground mb-2">{t('shiftTemplates.tasksForShift')}:</p>
                                    <ShiftTasksPreview tasks={filteredTasks} totalTaskInstances={totalTaskInstances} />
                                </div>


                                {/* Swap Option - Only show if NOT already swapping */}
                                {!swapTemplateId && (
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="w-full mt-2 border-dashed border-primary/30 hover:border-primary hover:bg-primary/5 text-primary/80"
                                        onClick={() => navigate(`/app/patients/${patientId}/shift-swap`)}
                                    >
                                        <ArrowRightLeft className="w-4 h-4 mr-2" />
                                        {t('shiftSwap.swapShift')}
                                    </Button>
                                )}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Last Shift Info */}
                {!loadingLastShift && lastShift && (
                    <Card className="border-primary/20 bg-primary/5 shadow-sm">
                        <CardHeader className="pb-2 pt-3">
                            <CardTitle className="text-sm flex items-center gap-2 font-medium text-primary">
                                <FileText className="w-4 h-4" />
                                {t('shift.lastShift')}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="pb-3">
                            <div className="space-y-2">
                                {/* Caregiver - horizontal pattern like PatientDetailsCard */}
                                <div className="flex items-center gap-2 text-sm">
                                    <User className="w-4 h-4 text-muted-foreground shrink-0" />
                                    <span className="text-muted-foreground">{t('shift.caregiver')}:</span>
                                    <span className="font-medium">{lastShift.caregiver_name || t('shift.notIdentified')}</span>
                                </div>

                                {/* Period - horizontal pattern */}
                                <div className="flex items-center gap-2 text-sm">
                                    <Clock className="w-4 h-4 text-muted-foreground shrink-0" />
                                    <span className="text-muted-foreground">{t('shift.period')}:</span>
                                    <span className="font-medium">
                                        {format(parseISO(lastShift.check_in_at), 'dd/MM HH:mm', { locale: dateLocale })}
                                        {lastShift.check_out_at && (
                                            <> → {format(parseISO(lastShift.check_out_at), 'HH:mm', { locale: dateLocale })}</>
                                        )}
                                    </span>
                                </div>
                            </div>
                            {/* Summaries Section */}
                            <div className="mt-2 space-y-3">
                                {lastShift.handoff && (
                                    <div className="bg-background/60 p-3 rounded-lg border border-primary/10">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-xs font-medium text-primary mb-1 block">{t('shift.caregiverNote')}</span>
                                            <div className="flex items-center gap-3 text-xs">
                                                <div className="flex items-center gap-1.5" title={t('shift.tasksCompleted')}>
                                                    <CheckCircle2 className="w-3.5 h-3.5 text-success" />
                                                    <span className="font-medium">{lastShift.handoff.tasks_completed}/{lastShift.handoff.tasks_total}</span>
                                                </div>
                                                {lastShift.handoff.sporadic_meds_given > 0 && (
                                                    <div className="flex items-center gap-1.5" title={t('shift.medsGiven')}>
                                                        <Pill className="w-3.5 h-3.5 text-primary" />
                                                        <span className="font-medium">{lastShift.handoff.sporadic_meds_given}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {lastShift.handoff.text_summary ? (
                                            <p className="text-sm text-foreground/90 italic border-l-2 border-primary/20 pl-2">
                                                "{lastShift.handoff.text_summary}"
                                            </p>
                                        ) : (
                                            <p className="text-xs text-muted-foreground italic pl-2">{t('shift.noCaregiverNote')}</p>
                                        )}
                                    </div>
                                )}

                                {lastShift.ai_summary && (
                                     <div className="bg-sporadic/10 p-3 rounded-lg border border-sporadic/20">
                                        <span className="text-xs font-medium text-sporadic mb-1 block flex items-center gap-1">
                                            <Activity className="w-3 h-3" />
                                            {t('shift.aiSummary')}
                                        </span>
                                        <p className="text-sm text-foreground/90 leading-relaxed">
                                            {lastShift.ai_summary}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Location Status */}
                <div className="flex items-center gap-2 text-sm px-1 justify-center bg-muted/10 p-2 rounded-md">
                    <MapPin className={`w-4 h-4 ${locationError ? 'text-destructive' : 'text-primary'}`} />
                    {locationLoading ? (
                        <span className="text-muted-foreground">{t('shift.gettingLocation')}</span>
                    ) : locationError ? (
                        <span className="text-destructive font-medium">{locationError}</span>
                    ) : (
                        <span className="text-success font-medium">{t('shift.locationObtained')}</span>
                    )}
                </div>

                {!isPatientActive && patientStatus && (
                    <div className="p-3 rounded-lg border border-warning bg-warning/10 text-sm">
                        {patientStatus === 'archived'
                            ? t('patient.archivedReadOnly', 'Paciente arquivado: o início de plantão está desabilitado.')
                            : t('patient.suspendedReadOnly', 'Paciente suspenso: reative a assinatura para iniciar o plantão.')}
                    </div>
                )}
                {/* Espaçador para o conteúdo não ficar sob a barra fixa */}
                <div className="h-28 lg:h-24" aria-hidden />
            </div>

            {/* Ação principal fixa: sempre visível, sem precisar rolar a lista toda */}
            <div className="fixed left-0 right-0 bottom-[calc(4.75rem+env(safe-area-inset-bottom))] lg:bottom-0 lg:left-auto lg:right-auto lg:w-full lg:max-w-5xl z-30 border-t border-border/60 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 px-4 py-3 lg:pb-[calc(0.75rem+env(safe-area-inset-bottom))]">

                <Button
                    onClick={handleSubmit}
                    disabled={isLoading || locationLoading || loadingPreview || !isPatientActive}
                    className="w-full h-14 text-lg font-semibold shadow-lg active:scale-[0.98] transition-all"
                    size="lg"
                >
                    {isLoading ? (
                        <Loader2 className="w-6 h-6 mr-2 animate-spin" />
                    ) : (
                        <LogIn className="w-6 h-6 mr-2" />
                    )}
                    {t('shift.confirmCheckIn')}
                </Button>
            </div>
        </AppLayout >
    );
}
